// Template gallery — the server's list (tenant templates first, then platform,
// latest version each) as cards. "Use" opens InstantiateDialog and posts
// /programs/from-template; onCreated receives the new DRAFT program so the
// caller can open it. "Preview" hands the template to the caller.
import { useT } from '../lib/i18n';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import type { Program, ProgramTemplate } from '@/services/learningService';
import { useCreateProgramFromTemplate, useProgramTemplates } from '../api/hooks';
import { InstantiateDialog } from './InstantiateDialog';

interface Props {
  onCreated: (program: Program) => void;
  onPreview?: (template: ProgramTemplate) => void;
  selectedId?: string | null;
  className?: string;
}

export const TemplateGallery: React.FC<Props> = ({ onCreated, onPreview, selectedId, className }) => {
  const t = useT();
  const q = useProgramTemplates();
  const create = useCreateProgramFromTemplate();
  const [using, setUsing] = useState<ProgramTemplate | null>(null);
  const templates = q.data ?? [];
  return (
    <div className={`space-y-2 ${className || ''}`} data-testid="template-gallery">
      {q.isLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('training:loading', 'Loading')}…</div>}
      {q.isError && <p className="text-sm text-red-600 dark:text-red-400">{getApiErrorMessage(q.error, t('training:tpl_unavailable', 'Templates could not be loaded.'))}</p>}
      {!q.isLoading && !q.isError && templates.length === 0 && <p className="text-sm text-muted-foreground">{t('training:tpl_none', 'No templates yet — save a program as a template to start one.')}</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        {templates.map((tpl) => (
          <div key={tpl.id} data-testid={`template-card-${tpl.id}`}
            className={`rounded-md border p-3 space-y-1.5 text-sm ${selectedId === tpl.id ? 'border-orange-400 bg-orange-50/50 dark:bg-orange-500/5' : 'border-border'}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-foreground">{tpl.name}</span>
              <span data-testid="template-owner" className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${tpl.platform ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/[0.16] dark:text-blue-300' : 'bg-success-soft text-success-strong'}`}>
                {tpl.platform ? t('training:tpl_platform', 'Platform') : t('training:tpl_yours', 'Yours')}
              </span>
            </div>
            {tpl.description && <p className="text-xs text-muted-foreground line-clamp-2">{tpl.description}</p>}
            <div className="text-xs text-muted-foreground">
              {tpl.program_type || t('training:wiz_no_type', 'No type')} · {t('training:sessions_n', '{{n}} session(s)', { n: (tpl.definition?.sessions || []).length })} · v{tpl.version}
            </div>
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              <Button size="sm" onClick={() => setUsing(tpl)} disabled={create.isPending} data-testid={`template-use-${tpl.id}`}>{t('training:tpl_use', 'Use template')}</Button>
              {onPreview && <Button size="sm" variant="outline" onClick={() => onPreview(tpl)}>{t('training:tpl_preview', 'Preview')}</Button>}
            </div>
          </div>
        ))}
      </div>
      <InstantiateDialog template={using} busy={create.isPending} onClose={() => setUsing(null)}
        onConfirm={(body) => create.mutate(body, {
          onSuccess: (p) => { toast.success(t('training:tpl_draft_created', 'Draft created from template — set the audience and publish')); setUsing(null); onCreated(p); },
          // Without this the dialog just sat there on a refusal. The server was
          // already saying "a program with that name already exists" — the name
          // defaults to the template's, so the SECOND use of any template hits
          // it — and none of that reached the person waiting.
          onError: (e) => toast.error(getApiErrorMessage(e, t('training:tpl_create_failed', 'Could not create the program from this template.')), { duration: 8000 }),
        })} />
    </div>
  );
};

export default TemplateGallery;
